// src/components/layouts/app/Header.tsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import HeaderDesktop from "./sub/desktop/HeaderDesktop";
import HeaderMobile from "./sub/mobile/HeaderMobile";
import MobileDrawer from "./sub/mobile/MobileDrawer";
import MobileBottomNav from "./sub/mobile/MobileBottomNav";
import { CategoryItem } from "./sub/types";

const HeaderComponent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const [isSearchVisible, setIsSearchVisible] = useState(false);

  // Mise à jour des catégories pour utiliser la nouvelle interface
  const categories: CategoryItem[] = [
    {
      id: "1",
      name: "Maison & Jardin",
      slug: "maison-jardin",
      description: "Meubles, déco, jardinage",
      badge: "Nouveau",
      subcategories: [
        { id: "1-1", name: "Meubles", slug: "meubles" },
        { id: "1-2", name: "Décoration", slug: "decoration" },
        { id: "1-3", name: "Luminaires", slug: "luminaires" },
        { id: "1-4", name: "Jardin", slug: "jardin" },
        { id: "1-5", name: "Cuisine", slug: "cuisine" },
        { id: "1-6", name: "Salle de Bain", slug: "salle-de-bain" },
      ],
    },
    {
      id: "2",
      name: "Électronique",
      slug: "electronique",
      description: "Smartphones, ordinateurs, TV",
      featured: true,
      subcategories: [
        { id: "2-1", name: "Smartphones", slug: "smartphones" },
        { id: "2-2", name: "Ordinateurs", slug: "ordinateurs" },
        { id: "2-3", name: "Audio & Hi-Fi", slug: "audio" },
        { id: "2-4", name: "Gaming", slug: "gaming" },
        { id: "2-5", name: "Télévisions", slug: "televisions" },
        { id: "2-6", name: "Accessoires", slug: "accessoires" },
      ],
    },
    {
      id: "3",
      name: "Mode Homme",
      slug: "mode-homme",
      description: "Vêtements, chaussures, accessoires",
      subcategories: [
        { id: "3-1", name: "Chemises", slug: "chemises" },
        { id: "3-2", name: "Pantalons", slug: "pantalons" },
        { id: "3-3", name: "Chaussures", slug: "chaussures" },
        { id: "3-4", name: "Accessoires", slug: "accessoires-homme" },
        { id: "3-5", name: "Sportswear", slug: "sportswear" },
        { id: "3-6", name: "Cravates & Ceintures", slug: "cravates-ceintures" },
      ],
    },
    {
      id: "4",
      name: "Mode Femme",
      slug: "mode-femme",
      description: "Robes, sacs, bijoux",
      badge: "Tendance",
      subcategories: [
        { id: "4-1", name: "Robes", slug: "robes" },
        { id: "4-2", name: "Chaussures", slug: "chaussures-femme" },
        { id: "4-3", name: "Sacs", slug: "sacs" },
        { id: "4-4", name: "Bijoux", slug: "bijoux" },
        { id: "4-5", name: "Lingerie", slug: "lingerie" },
        { id: "4-6", name: "Maillots de Bain", slug: "maillots-de-bain" },
      ],
    },
    {
      id: "5",
      name: "Beauté & Santé",
      slug: "beaute-sante",
      description: "Cosmétiques, soins, bien-être",
      subcategories: [
        { id: "5-1", name: "Maquillage", slug: "maquillage" },
        { id: "5-2", name: "Soins Visage", slug: "soins-visage" },
        { id: "5-3", name: "Soins Corps", slug: "soins-corps" },
        { id: "5-4", name: "Parfums", slug: "parfums" },
        { id: "5-5", name: "Santé", slug: "sante" },
        { id: "5-6", name: "Cheveux", slug: "cheveux" },
      ],
    },
    {
      id: "6",
      name: "Sport & Loisirs",
      slug: "sport-loisirs",
      description: "Équipements, vêtements sport",
      subcategories: [
        { id: "6-1", name: "Fitness", slug: "fitness" },
        { id: "6-2", name: "Running", slug: "running" },
        { id: "6-3", name: "Yoga & Pilates", slug: "yoga-pilates" },
        { id: "6-4", name: "Sports Collectifs", slug: "sports-collectifs" },
        { id: "6-5", name: "Randonnée", slug: "randonnee" },
        { id: "6-6", name: "Vélos", slug: "velos" },
      ],
    },
    {
      id: "7",
      name: "Alimentation",
      slug: "alimentation",
      description: "Produits alimentaires bio",
      subcategories: [
        { id: "7-1", name: "Bio & Naturel", slug: "bio-naturel" },
        { id: "7-2", name: "Épicerie", slug: "epicerie" },
        { id: "7-3", name: "Boissons", slug: "boissons" },
        { id: "7-4", name: "Snacks", slug: "snacks" },
        { id: "7-5", name: "Surgelés", slug: "surgeles" },
        { id: "7-6", name: "Compléments", slug: "complements" },
      ],
    },
    {
      id: "8",
      name: "Enfants & Bébé",
      slug: "enfants-bebe",
      description: "Vêtements, jouets, puériculture",
      badge: "Promo",
      subcategories: [
        { id: "8-1", name: "Vêtements", slug: "vetements-enfants" },
        { id: "8-2", name: "Jouets", slug: "jouets" },
        { id: "8-3", name: "Puériculture", slug: "puericulture" },
        { id: "8-4", name: "Chambre", slug: "chambre-enfant" },
        { id: "8-5", name: "Scolaire", slug: "scolaire" },
        { id: "8-6", name: "Éveil", slug: "eveil" },
      ],
    },
  ];

  // Pour la navigation simple (compatibilité)
  const mainCategories = categories.map(cat => cat.name);

  const getCategorySlug = (cat: string) =>
    cat.toLowerCase().replace(/ & /g, "-").replace(/ /g, "-");

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setIsMenuOpen(false);
      setIsSearchVisible(false);
    }
  };

  const handleCategorySelect = (category: CategoryItem) => {
    navigate(`/category/${category.slug}`);
    console.log('Catégorie sélectionnée:', category.name);
  };

  return (
    <>
      <HeaderDesktop
        categories={categories}
        mainCategories={mainCategories}
        handleSearch={handleSearch}
        getCategorySlug={getCategorySlug}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        isUserMenuOpen={isUserMenuOpen}
        setIsUserMenuOpen={setIsUserMenuOpen}
        isCategoryDropdownOpen={isCategoryDropdownOpen}
        setIsCategoryDropdownOpen={setIsCategoryDropdownOpen}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        scrolled={scrolled}
        isSearchVisible={isSearchVisible}
        setIsSearchVisible={setIsSearchVisible}
        onCategorySelect={handleCategorySelect}
      />

      <HeaderMobile
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        isSearchVisible={isSearchVisible}
        setIsSearchVisible={setIsSearchVisible}
        setIsMenuOpen={setIsMenuOpen}
        handleSearch={handleSearch}
        scrolled={scrolled}
      />

      <MobileBottomNav
        location={location}
        onAccountClick={() => setIsMenuOpen(true)}
      />
      <MobileDrawer
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        categories={categories}
        getCategorySlug={getCategorySlug}
      />
    </>
  );
};

export default HeaderComponent;